import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountSummaryBloc } from '@principle-theorem/ng-principle-shared';
import {
  type IPatient,
  isPatient,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef, isWithRef, snapshot } from '@principle-theorem/shared';
import { of } from 'rxjs';
import { ManagementResource } from '../models/management-resource';
import {
  type IManagementTool,
  type IToolRunContext,
} from '../models/management-tool';

function isPatientWithRef(value: unknown): value is WithRef<IPatient> {
  return isPatient(value) && isWithRef<IPatient>(value);
}

@Injectable({ providedIn: 'root' })
export class RecalculateAccountSummaryTool implements IManagementTool {
  private _snackBar = inject(MatSnackBar);

  readonly name = 'recalculate-account-summary';
  readonly label = 'Recalculate Account Summary';
  readonly icon = 'calculate';
  readonly description =
    'Recalculate the account summary for the current patient';
  readonly resource = ManagementResource.Patient;

  async run(context: IToolRunContext): Promise<void> {
    if (!context.resource) {
      return;
    }
    const value = await snapshot(context.resource.value$);
    if (!value || !isPatientWithRef(value)) {
      return;
    }
    const bloc = new AccountSummaryBloc(of(value));
    try {
      await bloc.recalculateSummary();
      this._snackBar.open('Account summary recalculated successfully', 'OK', {
        duration: 3000,
      });
    } catch {
      this._snackBar.open('Failed to recalculate account summary', 'OK', {
        duration: 5000,
      });
    }
  }
}
